import { useState, type ComponentPropsWithoutRef } from 'react'
import { useSteleBreakpoint } from '@/src/hooks/useBreakpoint'
import ButtonIcon from './ButtonIcon'
import IconChevron from '../icons/IconChevron'
import IconChamele from '../icons/IconChamele'
import clsx from 'clsx'
import ButtonFlash from './ButtonFlash'
import { useRunState } from '@/src/game/provider/useRunState'
import { motion } from 'motion/react'

type GameControlPanelProps = ComponentPropsWithoutRef<'button'> & {
    onPointerDownRight: () => void;
    onPointerDownLeft: () => void;
    onSnap: () => void;
    onJump: () => void;
    snapDisabled?: boolean;
    batteryCount?: number;
}

export default function GameControlPanel({ onSnap, onJump, snapDisabled, batteryCount = 0, onPointerDownRight, onPointerDownLeft, ...buttonProps }: GameControlPanelProps) {
    const [isPressed, setIsPressed] = useState<boolean>(false)
    const isStele = useSteleBreakpoint();

    const runState = useRunState()
    const snapshot = runState.snapshot
    const isShielded =
        snapshot.shieldActive &&
        snapshot.lifes > 0

    const [starAnimationKey, setStarAnimationKey] = useState(0)

    const PLANE_TIMER_MAX = 5
    const planeProgress = snapshot.planeTimer > 0
        ? snapshot.planeTimer / PLANE_TIMER_MAX
        : 0
    const isStarPowerUpActive = snapshot.planeTimer > 0

    return (
        <div
            className={clsx(
                'absolute flex justify-between w-full',
                'bottom-0 px-4',
                'stele:bottom-0 stele:px-20'
            )}>
            <ButtonIcon
                icon={<IconChevron width='100%' height='100%' />}
                variant='move'
                color='blue'
                onPointerDown={(e) => {
                    if (e.pointerType === 'touch') {
                        e.preventDefault()
                    }
                    e.currentTarget.setPointerCapture(e.pointerId)
                    setIsPressed(true)
                    onPointerDownLeft()
                }}
                onPointerUp={(e) => {
                    setIsPressed(false)
                    try {
                        e.currentTarget.releasePointerCapture(e.pointerId)
                    } catch {
                        /* not captured */
                    }
                }}
                onPointerLeave={(e) => {
                    setIsPressed(false)
                    try {
                        e.currentTarget.releasePointerCapture(e.pointerId)
                    } catch {
                        /* not captured */
                    }
                }}
                onPointerCancel={(e) => {
                    setIsPressed(false)
                    try {
                        e.currentTarget.releasePointerCapture(e.pointerId)
                    } catch {
                        /* not captured */
                    }
                }}
            />
            <div className='flex flex-col justify-center items-center pt-6 stele:pt-24'>
                <div className='bottom-20 stele:bottom-80 absolute flex flex-col items-center gap-2'>
                    <div className='flex justify-center items-center gap-2 stele:gap-8'>
                        {/* <div className={clsx(
                            'flex justify-center items-center bg-black-stroke/15 rounded-full w-11 stele:w-64 h-11 stele:h-64',
                            !isShielded ? 'opacity-0 pointer-events-none' : 'opacity-100 pointer-events-auto',
                        )}
                        >
                            <img
                                src='/images/powerups/PowerUp_Shield_Shift.png'
                                className={clsx(
                                    'w-10 h-10',
                                    'stele:w-60 stele:h-60',

                                )}
                            />
                        </div> */}
                        {isStarPowerUpActive &&
                            <motion.div
                                initial={{ opacity: 0, y: -50 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.7, duration: 0.5 }}
                            >
                                <div
                                    className={clsx(
                                        'z-50 flex justify-center items-center',
                                        'w-56.75 h-11',
                                        'stele:w-227 stele:h-44'
                                    )}
                                >
                                    <div
                                        className={clsx(
                                            'relative flex justify-center items-center',
                                            'w-56.75 h-11',
                                            'stele:w-227 stele:h-44'
                                        )}
                                    >
                                        <svg
                                            width={isStele ? 908 : 227}
                                            height={isStele ? 128 : 32}
                                            viewBox='0 0 227 32'
                                            className='absolute inset-0 w-full h-full'
                                            aria-hidden='true'
                                            fill='none'
                                            xmlns='http://www.w3.org/2000/svg'
                                        >
                                            <defs>
                                                <linearGradient id='powerup-bar-fill-base' x1='0' y1='16' x2='227' y2='16' gradientUnits='userSpaceOnUse'>
                                                    <stop offset='0%' stopColor='rgb(121 18 62)' />
                                                    <stop offset='16%' stopColor='rgb(122 14 132)' />
                                                    <stop offset='32%' stopColor='rgb(74 12 150)' />
                                                    <stop offset='48%' stopColor='rgb(18 72 166)' />
                                                    <stop offset='66%' stopColor='rgb(14 110 49)' />
                                                    <stop offset='82%' stopColor='rgb(160 131 13)' />
                                                    <stop offset='92%' stopColor='rgb(162 87 16)' />
                                                    <stop offset='100%' stopColor='rgb(121 18 62)' />
                                                </linearGradient>
                                                <linearGradient id='powerup-bar-fill' x1='0' y1='16' x2='227' y2='16' gradientUnits='userSpaceOnUse'>
                                                    <stop offset='0%' stopColor='rgb(221 11 47)' />
                                                    <stop offset='16%' stopColor='rgb(255 0 212)' />
                                                    <stop offset='32%' stopColor='rgb(119 0 255)' />
                                                    <stop offset='48%' stopColor='rgb(0 102 255)' />
                                                    <stop offset='66%' stopColor='rgb(0 190 26)' />
                                                    <stop offset='82%' stopColor='rgb(255 221 0)' />
                                                    <stop offset='92%' stopColor='rgb(255 131 0)' />
                                                    <stop offset='100%' stopColor='rgb(221 11 47)' />
                                                </linearGradient>
                                                <clipPath id='powerup-bar-shape'>
                                                    <path d='M8.49664 13.2699C5.2337 13.8477 3.05701 16.9613 3.63483 20.2242C4.21265 23.4872 7.32623 25.6639 10.5892 25.086L9.54291 19.178L8.49664 13.2699ZM216.836 24.9846C220.1 25.5592 223.211 23.3794 223.786 20.1159C224.36 16.8524 222.181 13.741 218.917 13.1664L217.877 19.0755L216.836 24.9846ZM9.54291 19.178L10.5892 25.086C78.8063 13.0055 148.607 12.9712 216.836 24.9846L217.877 19.0755L218.917 13.1664C149.308 0.909867 78.0941 0.944901 8.49664 13.2699L9.54291 19.178Z' />
                                                </clipPath>
                                                <radialGradient id='powerup-bar-shine' cx='0' cy='0' r='1' gradientUnits='userSpaceOnUse' gradientTransform='translate(115.031 1212) rotate(-90) scale(1214.51 899.491)'>
                                                    <stop offset='0.989297' stopColor='white' stopOpacity='0' />
                                                    <stop offset='0.991735' stopColor='white' />
                                                </radialGradient>
                                            </defs>
                                            <path
                                                d='M9.5429 19.178C78.4502 6.9752 148.957 6.94051 217.877 19.0755'
                                                stroke='#E4F1FB'
                                                strokeWidth='16'
                                                strokeLinecap='round'
                                                strokeLinejoin='round'
                                            />
                                            <g clipPath='url(#powerup-bar-shape)'>
                                                <rect
                                                    x='0'
                                                    y='0'
                                                    width='227'
                                                    height='32'
                                                    fill='url(#powerup-bar-fill-base)'
                                                />
                                                <rect
                                                    x='0'
                                                    y='0'
                                                    width={227 * planeProgress}
                                                    height='32'
                                                    fill='url(#powerup-bar-fill)'
                                                    className='transition-[width] duration-150 ease-linear'
                                                />
                                            </g>
                                            <path
                                                d='M8.49664 13.2699C5.2337 13.8477 3.05701 16.9613 3.63483 20.2242C4.21265 23.4872 7.32623 25.6639 10.5892 25.086L9.54291 19.178L8.49664 13.2699ZM216.836 24.9846C220.1 25.5592 223.211 23.3794 223.786 20.1159C224.36 16.8524 222.181 13.741 218.917 13.1664L217.877 19.0755L216.836 24.9846ZM9.54291 19.178L10.5892 25.086C78.8063 13.0055 148.607 12.9712 216.836 24.9846L217.877 19.0755L218.917 13.1664C149.308 0.909867 78.0941 0.944901 8.49664 13.2699L9.54291 19.178Z'
                                                fill='#8A0C01'
                                                fillOpacity='0.18'
                                            />
                                            <path
                                                d='M9.36881 18.1856C78.391 5.97268 149.016 5.93797 218.05 18.083'
                                                stroke='url(#powerup-bar-shine)'
                                                strokeWidth='4'
                                                strokeLinecap='round'
                                                strokeLinejoin='round'
                                                opacity='0.65'
                                            />
                                        </svg>

                                    </div>
                                </div>
                            </motion.div>
                        }
                    </div>
                    {!snapDisabled &&
                        <motion.div
                            initial={{ opacity: 0, y: -50 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 1, duration: 0.3 }}
                        >
                            <ButtonFlash
                                batteryCount={batteryCount}
                                onClick={() => onSnap()}
                            />
                        </motion.div>
                    }
                </div>
                <ButtonIcon
                    icon={<IconChamele width='100%' height='100%' />}
                    variant='move'
                    color='orange'
                    onPointerDown={(e) => {
                        if (e.pointerType === 'touch') {
                            e.preventDefault()
                        }
                        e.currentTarget.setPointerCapture(e.pointerId)
                        setIsPressed(true)
                        onJump()
                    }}
                    onPointerUp={(e) => {
                        setIsPressed(false)
                        try {
                            e.currentTarget.releasePointerCapture(e.pointerId)
                        } catch {
                            /* not captured */
                        }
                    }}
                    onPointerLeave={(e) => {
                        setIsPressed(false)
                        try {
                            e.currentTarget.releasePointerCapture(e.pointerId)
                        } catch {
                            /* not captured */
                        }
                    }}
                    onPointerCancel={(e) => {
                        setIsPressed(false)
                        try {
                            e.currentTarget.releasePointerCapture(e.pointerId)
                        } catch {
                            /* not captured */
                        }
                    }}
                />
            </div>
            <ButtonIcon
                icon={<IconChevron width='100%' height='100%' className='rotate-180' />}
                variant='move'
                color='blue'
                onPointerDown={(e) => {
                    if (e.pointerType === 'touch') {
                        e.preventDefault()
                    }
                    e.currentTarget.setPointerCapture(e.pointerId)
                    setIsPressed(true)
                    onPointerDownRight()
                }}
                onPointerUp={(e) => {
                    setIsPressed(false)
                    try {
                        e.currentTarget.releasePointerCapture(e.pointerId)
                    } catch {
                        /* not captured */
                    }
                }}
                onPointerLeave={(e) => {
                    setIsPressed(false)
                    try {
                        e.currentTarget.releasePointerCapture(e.pointerId)
                    } catch {
                        /* not captured */
                    }
                }}
                onPointerCancel={(e) => {
                    setIsPressed(false)
                    try {
                        e.currentTarget.releasePointerCapture(e.pointerId)
                    } catch {
                        /* not captured */
                    }
                }}
            />
        </div>
    )
}
